import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  Loader2,
  ArrowRight,
  Lock,
  ExternalLink,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import { Logo } from "@/components/logo";
import { LogoRotator } from "@/components/logo-rotator";
import { saveCheckHandoff } from "@/lib/check-handoff";
import {
  runRedditGapCheck,
  submitRedditGapEmail,
  REDDIT_GAP_FREE_THREADS,
  type RedditGapResult,
} from "@/lib/reddit-gap.functions";
import type { GapThread } from "@/lib/reddit-gap.server";
import { RulesReminderBanner, SubredditRulesChip } from "@/components/reddit-rules-note";
import { ThreadScoreBreakdown } from "@/components/thread-score-breakdown";



const TITLE = "Free Reddit Visibility Gap Check, where your competitors get talked about | monroya.ai";
const DESCRIPTION =
  "Find the live Reddit threads where buyers discuss your competitors and you're never mentioned. Free, no signup, real engagement data, archived threads filtered out.";

export const Route = createFileRoute("/reddit-check")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Which Reddit threads recommend your competitors, and not you?" },
      { property: "og:description", content: "Free Reddit visibility gap check. No signup. ~30 seconds." },
      { property: "og:url", content: "https://www.monroya.ai/reddit-check" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Which Reddit threads recommend your competitors, and not you?" },
      { name: "twitter:description", content: "Free Reddit visibility gap check. No signup. ~30 seconds." },
    ],
    links: [{ rel: "canonical", href: "https://www.monroya.ai/reddit-check" }],
  }),
  component: RedditCheckPage,
});

const SCAN_STEPS = [
  "Reading your site to work out your category…",
  "Asking an AI model who it recommends in that category…",
  "Extracting the competitor names it actually returned…",
  "Searching Reddit for threads about those competitors…",
  "Pulling real upvotes, comments, and post age…",
  "Dropping archived and locked threads, they can't take replies…",
  "Checking which threads never mention you…",
  "Almost there, ranking the best openings…",
];

function ScanProgress() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => Math.min(s + 1, SCAN_STEPS.length - 1)), 4500);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="mx-auto mt-6 max-w-xl rounded-xl border border-border bg-panel px-5 py-4 text-left">
      <p className="flex items-center gap-2 text-sm font-medium text-ink">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        {SCAN_STEPS[step]}
      </p>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-border">
        <div
          className="brand-gradient h-full rounded-full transition-all duration-700"
          style={{ width: `${((step + 1) / SCAN_STEPS.length) * 100}%` }}
        />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        This usually takes 30–60 seconds. We're running a live AI query and real Reddit lookups, not
        a cached list.
      </p>
    </div>
  );
}

/**
 * The competitor list is whatever an AI model actually names for this
 * category. Whether the company itself shows up in that answer is a signal in
 * its own right, so it's shown, not just used silently as search input.
 */
function CategorySignal({ result }: { result: RedditGapResult }) {
  const competitors = result.competitors ?? [];
  if (competitors.length === 0) return null;

  // When the visitor named the competitors themselves, there's no AI answer to
  // report on — just confirm what we searched against.
  if (result.competitorSource === "user") {
    return (
      <div className="mt-6 rounded-2xl border border-border bg-white p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-signal-teal-ink" />
          <div className="min-w-0">
            <h3 className="font-display text-lg font-bold text-ink">
              Checked against the competitors you named
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Every thread below mentions one of these companies and never mentions {result.brand}.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {competitors.map((c) => (
                <span key={c} className="stamp bg-panel text-xs font-medium text-ink">
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const named = result.brandNamedInAnswer === true;
  return (
    <div
      className={`mt-6 rounded-2xl border p-6 sm:p-8 ${
        named ? "border-border bg-white" : "border-signal-amber/40 bg-signal-amber/5"
      }`}
    >
      <div className="flex items-start gap-3">
        {named ? (
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-signal-teal-ink" />
        ) : (
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-signal-amber-dark" />
        )}
        <div className="min-w-0">
          <h3 className="font-display text-lg font-bold text-ink">
            {named
              ? `AI named ${result.brand} in its own category${
                  result.brandRank ? ` - position ${result.brandRank}` : ""
                }`
              : `AI didn't name ${result.brand} as an option in ${result.category}`}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            We asked an AI model the buyer question below, then used the brands it named to search
            Reddit, so every thread here is anchored to a real competitor, not a keyword guess.
          </p>
          {result.buyerPrompt && (
            <p className="mt-3 rounded-lg bg-panel px-3 py-2 text-sm italic text-ink">
              "{result.buyerPrompt}"
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {competitors.map((c) => (
              <span key={c} className="stamp bg-panel text-xs font-medium text-ink">
                {c}
              </span>
            ))}
          </div>
          {!named && (
            <p className="mt-3 text-sm text-ink">
              That's a miscategorization gap: buyers asking this question never see {result.brand} at
              all. Fixing it is a different job from Reddit, the full check shows why.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadCard({ thread, index }: { thread: GapThread; index: number }) {
  return (
    <div className="rounded-xl border border-border bg-white p-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 font-mono text-xs text-muted-foreground">{index + 1}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="stamp bg-panel text-xs font-medium text-ink">r/{thread.subreddit}</span>
            {thread.competitors.map((c) => (
              <span
                key={c}
                className="rounded-full bg-signal-coral/10 px-2.5 py-0.5 text-xs font-medium text-signal-coral-ink"
              >
                {c} mentioned
              </span>
            ))}
          </div>

          <a
            href={thread.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 flex items-start gap-1.5 font-medium text-ink hover:text-[color:var(--brand-accent-ink)]"
          >
            <span className="min-w-0">{thread.title}</span>
            <ExternalLink className="mt-1 h-3.5 w-3.5 shrink-0" />
          </a>

          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <SubredditRulesChip subreddit={thread.subreddit} />


            {thread.verified ? (
              <span className="inline-flex items-center gap-1 text-signal-teal-ink">Open to new replies</span>
            ) : (
              <span className="text-muted-foreground/80">
                reply status unverified, check the thread before replying
              </span>
            )}
          </div>

          <ThreadScoreBreakdown thread={thread} />

        </div>
      </div>
    </div>
  );
}

function RedditCheckPage() {
  const runScan = useServerFn(runRedditGapCheck);
  const saveEmail = useServerFn(submitRedditGapEmail);
  const navigate = useNavigate();

  const [domain, setDomain] = useState("");
  const [competitors, setCompetitors] = useState(["", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RedditGapResult | null>(null);
  const [email, setEmail] = useState("");
  const [handingOff, setHandingOff] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const res = await runScan({
        data: {
          domain,
          competitors: competitors.map((c) => c.trim()).filter(Boolean),
        },
      });
      if (res.status === "ok") setResult(res);
      else {
        setResult(res.status === "no_threads" ? res : null);
        setError(res.message ?? "Something went wrong. Try again in a moment.");
      }
    } catch {
      setError("Something went wrong. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  }

  async function onEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !result) return;
    setHandingOff(true);
    if (result.id) {
      try {
        await saveEmail({ data: { id: result.id, email } });
      } catch {
        /* best effort — never block the handoff */
      }
    }
    saveCheckHandoff({
      email,
      domain: result.domain ?? domain.trim(),
      brand: result.brand,
      category: result.category,
      competitors: result.competitors ?? [],
    });
    navigate({ to: "/onboarding", search: { email } as never });
  }

  const threads = result?.threads ?? [];
  const visible = threads.slice(0, REDDIT_GAP_FREE_THREADS);
  const gated = threads.slice(REDDIT_GAP_FREE_THREADS);

  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-border">
        <div className="flex site-container items-center justify-between px-5 sm:px-6 py-4">
          <Logo height={32} />
        </div>
      </header>

      <main>
        <section className="px-4 py-12 sm:px-6 sm:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="font-display text-3xl font-bold leading-tight tracking-tight text-ink sm:text-5xl">
              Which Reddit threads recommend {result?.brand ? result.brand : "your competitors"} - {" "}
              <span className="brand-gradient-text">and never mention you?</span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground sm:text-xl">
              Reddit is one of the most-cited sources in AI answers. We find live threads where your
              category is being decided without you. No signup. ~30 seconds.
            </p>

            <form onSubmit={onSubmit} className="mx-auto mt-8 flex max-w-2xl flex-col gap-3">
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  type="text"
                  aria-label="Your website domain"
                  inputMode="url"
                  autoComplete="url"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="yourcompany.com"
                  className="flex-1 rounded-lg border border-border bg-white px-4 py-3 text-base text-ink placeholder:text-muted-foreground focus:border-[color:var(--brand-accent)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-accent)]/20"
                  required
                  disabled={loading}
                />
              </div>

              <div className="rounded-xl border border-border bg-panel p-4 text-left">
                <label className="text-sm font-medium text-ink">
                  Name up to 3 competitors <span className="text-muted-foreground">(optional)</span>
                </label>
                <p className="mt-1 text-xs text-muted-foreground">
                  We'll search Reddit for threads that recommend these specific companies. Leave
                  blank and we'll ask an AI model who it names in your category instead.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  {competitors.map((c, i) => (
                    <input
                      key={i}
                      type="text"
                      aria-label={`Competitor ${i + 1}`}
                      value={c}
                      onChange={(e) =>
                        setCompetitors((prev) => prev.map((p, pi) => (pi === i ? e.target.value : p)))
                      }
                      placeholder={`Competitor ${i + 1}`}
                      className="rounded-lg border border-border bg-white px-3 py-2.5 text-sm text-ink placeholder:text-muted-foreground focus:border-[color:var(--brand-accent)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-accent)]/20"
                      disabled={loading}
                    />
                  ))}
                </div>
              </div>
              <button
                type="submit"
                disabled={loading || !domain.trim()}
                className="brand-gradient inline-flex items-center justify-center gap-2 self-center rounded-lg px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:opacity-95 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Searching Reddit…
                  </>
                ) : (
                  <>Find my Reddit gaps</>
                )}
              </button>
            </form>
            {loading && <ScanProgress />}

            {error && (
              <p className="mx-auto mt-4 max-w-xl rounded-lg border border-signal-coral/40 bg-signal-coral/10 px-4 py-2 text-sm text-signal-coral-ink">
                {error}
              </p>
            )}
          </div>
        </section>

        {result?.status === "ok" && threads.length > 0 && (
          <section className="px-4 pb-16 sm:px-6">
            <div className="mx-auto max-w-3xl">
              <div className="rounded-2xl border border-border bg-panel p-6 sm:p-8">
                <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  Reddit visibility gap
                </p>
                <h2 className="mt-2 font-display text-2xl font-bold text-ink">
                  {threads.length} open {threads.length === 1 ? "thread" : "threads"} in{" "}
                  {result.category} discuss your competitors, and not {result.brand}.
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Scanned {result.scanned} Reddit threads.
                  {result.excludedClosed ? ` Skipped ${result.excludedClosed} archived or locked (no replies possible).` : ""}
                  {result.excludedBrandPresent
                    ? ` Skipped ${result.excludedBrandPresent} that already mention you.`
                    : ""}
                </p>
              </div>

              <CategorySignal result={result} />



              {visible.length > 0 && (
                <div className="mt-6">
                  <RulesReminderBanner />
                </div>
              )}

              <div className="mt-4 space-y-4">

                {visible.map((t, i) => (
                  <ThreadCard key={t.id} thread={t} index={i} />
                ))}
              </div>

              {gated.length > 0 && (
                <div className="relative mt-4">
                  <div aria-hidden className="space-y-4 select-none blur-[5px]">
                    {gated.map((t, i) => (
                      <ThreadCard key={t.id} thread={t} index={i + REDDIT_GAP_FREE_THREADS} />
                    ))}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() =>
                        document
                          .getElementById("unlock-section")
                          ?.scrollIntoView({ behavior: "smooth", block: "center" })
                      }
                      className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-ink shadow-sm transition hover:border-[color:var(--brand-accent)]"
                    >
                      <Lock className="h-4 w-4" /> Unlock {gated.length} more{" "}
                      {gated.length === 1 ? "thread" : "threads"}
                    </button>
                  </div>
                </div>
              )}

              <div
                id="unlock-section"
                className="mt-8 rounded-2xl border border-border bg-panel p-6 sm:p-8"
              >
                <h2 className="font-display text-xl font-bold text-ink sm:text-2xl">
                  See every thread, and what to say in them
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Start your free plan to unlock the full thread list, ongoing Reddit monitoring, and
                  a drafted reply for each opportunity. No credit card required.
                </p>
                <form onSubmit={onEmailSubmit} className="mt-5 flex flex-col gap-3 sm:flex-row">
                  <input
                    type="email"
                    aria-label="Your work email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="flex-1 rounded-lg border border-border bg-white px-4 py-3 text-base text-ink placeholder:text-muted-foreground focus:border-[color:var(--brand-accent)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-accent)]/20"
                    required
                  />
                  <button
                    type="submit"
                    disabled={!email || handingOff}
                    className="brand-gradient inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:opacity-95 disabled:opacity-60"
                  >
                    {handingOff ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Continuing…
                      </>
                    ) : (
                      <>
                        Continue → <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>
                <p className="mt-3 text-xs text-muted-foreground">
                  We'll pre-fill your setup with the domain, category, and competitors we just detected.
                </p>
              </div>
            </div>
          </section>
        )}

        {result?.status === "no_threads" && (
          <section className="px-4 pb-16 sm:px-6">
            <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-panel p-6 text-center sm:p-8">
              <AlertTriangle className="mx-auto h-6 w-6 text-signal-amber-dark" />
              <h2 className="mt-3 font-display text-xl font-bold text-ink">No open gap found</h2>
              <p className="mt-2 text-sm text-muted-foreground">{result.message}</p>
              <div className="mt-6 text-left">
                <CategorySignal result={result} />
              </div>
              <Link
                to="/check"
                className="mt-5 inline-flex items-center gap-2 rounded-lg border border-border bg-white px-5 py-2.5 text-sm font-semibold text-ink transition hover:border-[color:var(--brand-accent)]"
              >
                Run the full AI visibility check <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </section>
        )}

        <section className="mt-4">
          <div className="site-container px-5 sm:px-6">
            <LogoRotator />
          </div>
        </section>
      </main>

      <footer className="mt-16 border-t border-border py-8">
        <div className="flex site-container flex-col items-center gap-5 px-5 sm:px-6 text-center">
          <a
            href="https://turbo0.com/item/reddit-gap-check"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <img
              src="https://img.turbo0.com/badge-listed-light.svg"
              alt="Listed on Turbo0"
              className="h-[54px] w-auto"
            />
          </a>
          <a
            href="https://startupbase.io/products/reddit-gap-check-by-monroya-ai?utm_source=startupbase&utm_medium=badge&utm_campaign=featured-badge-light"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <img
              src="https://statics.startupbase.io/site/badges/featured-on-sb.svg"
              alt="Featured on StartupBase"
              className="h-[55px] w-auto"
            />
          </a>
          <div className="flex w-full flex-col items-center gap-4 text-xs text-muted-foreground sm:flex-row sm:justify-between sm:text-left">
            <div>
              Want ongoing Reddit and AI citation tracking?{" "}
              <Link to="/" className="font-medium text-ink hover:text-[color:var(--brand-accent-ink)]">
                See Monroya →
              </Link>
            </div>
            <Link to="/check" className="font-medium text-ink hover:text-[color:var(--brand-accent-ink)]">
              Free AI-readiness check →
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
